import { useAssertedParams } from '@folio/lib';
import { CustomerFunnelModule } from '@folio/modules';
import { zodResolver } from '@hookform/resolvers/zod';
import { clientWithFork, useCustomerControllerEditCustomer } from 'korint-sdk';
import type {
  ConditionalVisibility,
  FormSection,
  FormSectionsWithShortcuts,
} from 'korint-sdk/react';
import { AlertTriangle, Car, Hash, Search } from 'lucide-react';
import { z } from 'zod';

const VEHICLE_COUNT = 5;

const showWhenHasVehicle = [
  {
    fieldName: 'hasVehicle',
    condition: 'equals',
    value: 'true',
  },
] satisfies ConditionalVisibility[];

const createVehicleSection = (vehicleNumber: number): FormSection => {
  const showWhenFillManually = [
    { fieldName: `fillManually${vehicleNumber}`, condition: 'equals' as const, value: 'true' },
  ] satisfies ConditionalVisibility[];
  const showWhenFillAutomatically = [
    { fieldName: `fillManually${vehicleNumber}`, condition: 'notEquals' as const, value: 'true' },
  ] satisfies ConditionalVisibility[];

  return {
    title: `Véhicule ${vehicleNumber}`,
    fields: [
      {
        name: `fillManually${vehicleNumber}`,
        required: false,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        defaultValue: 'false',
        icon: AlertTriangle,
        isCustomField: false,
        ignoreInPayload: true,
        apiDataAccessor: () => 'false',
      },
      {
        name: `manualRegistrationNumber${vehicleNumber}`,
        required: false,
        type: 'text',
        icon: Hash,
        DEPRECATED_placeholder: 'WW-000-AA',
        isCustomField: true,
        showWhen: showWhenFillManually,
        payloadKeyName: `registrationNumber${vehicleNumber}`,
        formDataAccessor: (formData: Record<string, unknown>) => {
          if (formData[`manualRegistrationNumber${vehicleNumber}`] === null) return '';
          return formData[`manualRegistrationNumber${vehicleNumber}`];
        },
      },
      {
        name: `manualBrand${vehicleNumber}`,
        required: false,
        type: 'vehicleBrand',
        icon: Car,
        isCustomField: true,
        showWhen: showWhenFillManually,
        payloadKeyName: `brand${vehicleNumber}`,
        formDataAccessor: (formData: Record<string, unknown>) => {
          if (formData[`manualBrand${vehicleNumber}`] === null) return '';
          return formData[`manualBrand${vehicleNumber}`];
        },
      },
      {
        name: `manualModel${vehicleNumber}`,
        required: false,
        type: 'vehicleModel',
        icon: Car,
        isCustomField: true,
        showWhen: showWhenFillManually,
        watchedFieldNames: { brand: `manualBrand${vehicleNumber}` },
        payloadKeyName: `model${vehicleNumber}`,
        formDataAccessor: (formData: Record<string, unknown>) => {
          if (formData[`manualModel${vehicleNumber}`] === null) return '';
          return formData[`manualModel${vehicleNumber}`];
        },
      },
      {
        name: `registrationNumber${vehicleNumber}`,
        required: false,
        type: 'vehicleRegistration',
        icon: Search,
        DEPRECATED_placeholder: 'AA-123-BB',
        isCustomField: true,
        showWhen: showWhenFillAutomatically,
      },
      {
        name: `brand${vehicleNumber}`,
        required: false,
        type: 'text',
        isCustomField: true,
        hidden: true,
        showWhen: showWhenFillAutomatically,
        formDataAccessor: (formData: Record<string, unknown>) => {
          if (formData[`brand${vehicleNumber}`] === null) return '';
          return formData[`brand${vehicleNumber}`];
        },
      },
      {
        name: `model${vehicleNumber}`,
        required: false,
        type: 'text',
        isCustomField: true,
        hidden: true,
        showWhen: showWhenFillAutomatically,
        formDataAccessor: (formData: Record<string, unknown>) => {
          if (formData[`model${vehicleNumber}`] === null) return '';
          return formData[`model${vehicleNumber}`];
        },
      },
    ],
    showWhen: showWhenHasVehicle,
  };
};

const sections = [
  {
    title: 'Véhicules',
    fields: [
      {
        name: 'hasVehicle',
        required: false,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Car,
        isCustomField: false,
        ignoreInPayload: true,
        apiDataAccessor: customer =>
          Array.from({ length: VEHICLE_COUNT }, (_, i) => i + 1).some(
            num => customer.customFields?.[`registrationNumber${num}`],
          )
            ? 'true'
            : 'false',
      },
    ],
  },
  ...Array.from({ length: VEHICLE_COUNT }, (_, i) => i + 1).map(createVehicleSection),
] as const satisfies FormSectionsWithShortcuts;

const generateVehicleValidationFields = () => {
  const fields: Record<string, z.ZodOptional<z.ZodNullable<z.ZodString>>> = {};

  for (let i = 1; i <= VEHICLE_COUNT; i++) {
    fields[`registrationNumber${i}`] = z.string().nullable().optional();
    fields[`brand${i}`] = z.string().nullable().optional();
    fields[`model${i}`] = z.string().nullable().optional();
    fields[`manualRegistrationNumber${i}`] = z.string().nullable().optional();
    fields[`manualBrand${i}`] = z.string().nullable().optional();
    fields[`manualModel${i}`] = z.string().nullable().optional();
  }

  return fields;
};

const vehicleValidationSchema = z.object({
  hasVehicle: z.string().optional(),
  ...generateVehicleValidationFields(),
});

/**
 * @debt can likely be moved to uniq config, setting the fields required and using showWhen instead of dynamic field array
 */
export function NewBusinessBrokerVehicleProtectionJuridique() {
  const { customerId, branchId } = useAssertedParams();

  const { mutateAsync: updateCustomer } = useCustomerControllerEditCustomer({
    client: { client: clientWithFork },
  });

  const afterSubmit = async (allFormValues: Record<string, unknown>) => {
    if (allFormValues.hasVehicle === 'false' || allFormValues.hasVehicle === false) {
      const clearFields = Array.from({ length: VEHICLE_COUNT }, (_, i) => {
        const vehicleNumber = i + 1;
        return [
          { key: `registrationNumber${vehicleNumber}`, value: '' },
          { key: `brand${vehicleNumber}`, value: '' },
          { key: `model${vehicleNumber}`, value: '' },
        ];
      }).flat();

      await updateCustomer({
        customerId,
        params: { branchId },
        data: { customFields: clearFields },
      });
    }
  };

  return (
    <CustomerFunnelModule
      sections={sections}
      path="vehicle"
      mode="stepper"
      customResolver={zodResolver(vehicleValidationSchema)}
      afterSubmit={afterSubmit}
    />
  );
}
